import { useMemo } from "react";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { useSelectedLensFolder, useSelectedLensFolderV3 } from "../../shared/hooks";
import { type PaginationAtomsKeys } from "../../shared/state";

import { type LensesOptions, useLenses } from "./useLenses";

type GetLensesQueryVariables = Pick<LensesOptions, "lensSpaceId" | "paginationAtomsKey" | "skip">;

const useGetLensesContainerQueryVariablesV3 = (
    paginationAtomsKey?: PaginationAtomsKeys,
    skip = false,
): GetLensesQueryVariables => {
    const selectedLensFolderV3 = useSelectedLensFolderV3();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const { lensFolder: lensFolderV2 } = useSelectedLensFolder();

    const selectedLensFolderId = isMyLensesOrgsV2Enabled ? lensFolderV2?.id : selectedLensFolderV3?.id;

    return useMemo(() => {
        const queryVariables = { lensSpaceId: selectedLensFolderId };

        return { ...queryVariables, paginationAtomsKey, skip };
    }, [selectedLensFolderId, paginationAtomsKey, skip]);
};

export type LensesOfSelectedContainerResult = Pick<
    ReturnType<typeof useLenses>,
    | "lenses"
    | "setLensSearchText"
    | "lensSearchText"
    | "filterBy"
    | "loadMore"
    | "hasMore"
    | "isFirstFetching"
    | "isReFetching"
>;

export const useLensesOfSelectedContainer = (
    paginationAtomsKey?: PaginationAtomsKeys,
    skip = false,
): LensesOfSelectedContainerResult => {
    const queryVariablesV3 = useGetLensesContainerQueryVariablesV3(paginationAtomsKey, skip);

    const { lenses, setLensSearchText, lensSearchText, loadMore, hasMore, isFirstFetching, isReFetching, filterBy } =
        useLenses(queryVariablesV3);

    return { lenses, setLensSearchText, lensSearchText, loadMore, hasMore, isFirstFetching, isReFetching, filterBy };
};
